import { Inject, LOCALE_ID, Pipe, PipeTransform } from '@angular/core';

/** Pipe for formatting large numbers with compact suffixes like 1m. */
@Pipe({
  name: 'compactNumber',
  pure: true,
  standalone: false,
})
export class CompactNumberPipe implements PipeTransform {
  private readonly formatters = new Map<number, Intl.NumberFormat>();
  private readonly maxCompactLength = 4;

  constructor(@Inject(LOCALE_ID) private locale: string) {}

  /** @inheritdoc */
  transform(
    value: number | string | null | undefined,
    maximumFractionDigits = 1
  ): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    const numericValue = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(numericValue)) {
      return String(value);
    }

    const formattedValue = this.normalizeFormattedValue(
      this.getFormatter(maximumFractionDigits).format(numericValue)
    );
    if (formattedValue.length <= this.maxCompactLength) {
      return formattedValue;
    }

    // The number was still to long to fit the length requirements so shorten it further.
    const shortenedValue = this.normalizeFormattedValue(
      this.getFormatter(0).format(numericValue)
    );
    return shortenedValue;
  }

  private getFormatter(maximumFractionDigits: number): Intl.NumberFormat {
    const normalizedFractionDigits = Math.max(0, maximumFractionDigits);
    const cachedFormatter = this.formatters.get(normalizedFractionDigits);
    if (cachedFormatter) {
      return cachedFormatter;
    }

    const formatter = new Intl.NumberFormat(this.locale, {
      compactDisplay: 'short',
      maximumFractionDigits: normalizedFractionDigits,
      notation: 'compact',
    });
    this.formatters.set(normalizedFractionDigits, formatter);
    return formatter;
  }

  private normalizeFormattedValue(value: string): string {
    return value.replace(/\s+/gu, '').replace(/[A-Z]+$/u, (match) => {
      return match.toLowerCase();
    });
  }
}
